import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FiX, FiLogIn, FiLogOut, FiClock, FiGlobe, FiCamera, FiExternalLink, FiMonitor, FiPrinter, FiDownload } from 'react-icons/fi';
import { toCyrillicFio } from '../utils/latinToCyrillicFio';
import { loadFrontendSettings } from '../utils/frontendSettings';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';

function formatTime(dtStr) {
  if (!dtStr) return '';
  
  // Проверяем, является ли время UTC (формат с Z в конце)
  const isUTC = /Z$/.test(dtStr) || dtStr.includes('T') && dtStr.includes('Z');
  
  let d;
  
  if (isUTC) {
    // Если время в UTC формате, парсим как UTC
    d = new Date(dtStr);
    if (isNaN(d.getTime())) {
      console.warn(`[formatTime] Failed to parse UTC time: "${dtStr}"`);
      return dtStr;
    }
    // Date объект автоматически конвертирует UTC в локальное время браузера
    const formatted = `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
    console.log(`[formatTime] UTC Input: "${dtStr}" -> Parsed as UTC: ${d.toISOString()} -> Local: ${d.toString()} -> Formatted: "${formatted}"`);
    return formatted;
  }
  
  // Парсим время как локальное (формат YYYY-MM-DD HH:mm:ss без часового пояса)
  const match = dtStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [_, year, month, day, hour, minute, second] = match;
    // Создаем Date объект с локальным временем (месяцы в JS с 0, поэтому -1)
    d = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    if (!isNaN(d.getTime())) {
      const formatted = `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
      console.log(`[formatTime] Local Input: "${dtStr}" -> Parsed as local: ${d.toISOString()} -> Formatted: "${formatted}"`);
      return formatted;
    }
  }
  
  // Fallback на стандартный парсинг
  d = new Date(dtStr);
  if (isNaN(d.getTime())) {
    console.warn(`[formatTime] Failed to parse: "${dtStr}"`);
    return dtStr; // Возвращаем оригинальную строку, если не удалось распарсить
  }
  const formatted = `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU')}`;
  console.log(`[formatTime] Fallback: "${dtStr}" -> ${d.toISOString()} -> "${formatted}"`);
  return formatted;
}

export default function UserWorkTimeDetailsMobile({
  open,
  onClose,
  logs,
  username,
  realUsername, // Правильный username для API запросов (например, "Ksendzik_Oleg")
  activityStats,
  urls = [],
  applications = [],
  screenshots = [],
  startDate,
  endDate,
  onOpenMobileSidebar,
}) {
  // КРИТИЧЕСКОЕ ЛОГИРОВАНИЕ В НАЧАЛЕ - сразу при получении props
  // ВЕРСИЯ 5.0 - ДОБАВЛЕН realUsername, УЛУЧШЕНА ОБРАБОТКА applications
  console.log('%c🚨🚨🚨 КОМПОНЕНТ UserWorkTimeDetailsMobile V5.0 - BUILD 2025-01-20 🚨🚨🚨', 'background: #ff0000; color: #ffffff; font-size: 18px; font-weight: bold; padding: 8px;');
  console.log('🚨🚨🚨 [UserWorkTimeDetailsMobile] ====== НАЧАЛО КОМПОНЕНТА V5.0 - BUILD 2025-01-20 ======');
  console.log('🚨🚨🚨 [UserWorkTimeDetailsMobile] ЕСЛИ ВЫ ВИДИТЕ ЭТОТ ЛОГ - НОВЫЙ КОД ЗАГРУЖЕН! 🚨🚨🚨');
  console.log('🚨 [UserWorkTimeDetailsMobile] Все props:', {
    open,
    username, // displayName для отображения
    realUsername, // правильный username для API (например, "Ksendzik_Oleg")
    urlsCount: urls?.length || 0,
    applicationsCount: applications?.length || 0,
    applications: applications,
    screenshotsCount: screenshots?.length || 0,
    screenshots: screenshots, // Добавляем полный массив для отладки
    hasActivityStats: !!activityStats,
    logsCount: logs?.length || 0,
    startDate,
    endDate
  });
  console.log('🚨 [UserWorkTimeDetailsMobile] applications (полностью):', applications);
  console.log('🚨 [UserWorkTimeDetailsMobile] screenshots (полностью):', screenshots);
  console.log('🚨 [UserWorkTimeDetailsMobile] realUsername для API:', realUsername);
  console.log('🚨 [UserWorkTimeDetailsMobile] ====================================');

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'urls', 'applications', 'screenshots'
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    screenshots: true,
    urls: true,
    applications: true,
    events: true
  });
  
  // Оптимизированное переключение вкладок
  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
  }, [activeTab]);
  const [localUrls, setLocalUrls] = useState(urls);
  const [localApplications, setLocalApplications] = useState(applications || []);
  const [localScreenshots, setLocalScreenshots] = useState(screenshots);
  const [localActivityStats, setLocalActivityStats] = useState(activityStats);
  const [, setSettingsVersion] = useState(0);
  useEffect(() => {
    const h = () => setSettingsVersion(v => v + 1);
    window.addEventListener('frontendSettingsUpdated', h);
    return () => window.removeEventListener('frontendSettingsUpdated', h);
  }, []);

  const headerColor = (loadFrontendSettings() || {}).modalHeaderTextColor || '#60a5fa';

  // Фильтруем данные по дате для надежности (объявляем ДО использования)
  const filterByDateRange = useCallback((items, timestampField = 'timestamp') => {
    if (!items || items.length === 0 || !startDate || !endDate) {
      console.log('🔍 [UserWorkTimeDetailsMobile] filterByDateRange: пропуск фильтрации (нет данных или дат)', {
        itemsCount: items?.length || 0,
        hasStartDate: !!startDate,
        hasEndDate: !!endDate
      });
      return items;
    }
    
    // Сравниваем только дату (без времени) для более надежной фильтрации
    // Преобразуем startDate и endDate в формат YYYY-MM-DD
    const startDateStr = startDate.split('T')[0]; // Берем только дату (YYYY-MM-DD)
    const endDateStr = endDate.split('T')[0];
    
    console.log('🔍 [UserWorkTimeDetailsMobile] filterByDateRange:', {
      itemsCount: items?.length || 0,
      timestampField,
      startDate,
      endDate,
      startDateStr,
      endDateStr
    });
    
    const filtered = items.filter(item => {
      if (!item[timestampField]) {
        console.warn('⚠️ [UserWorkTimeDetailsMobile] Элемент без timestamp:', item);
        return false;
      }
      const itemDate = new Date(item[timestampField]);
      if (isNaN(itemDate.getTime())) {
        console.warn('⚠️ [UserWorkTimeDetailsMobile] Неверная дата:', item[timestampField], item);
        return false;
      }
      
      // Извлекаем только дату из timestamp (YYYY-MM-DD)
      const itemDateStr = itemDate.toISOString().split('T')[0];
      
      // Сравниваем строки дат напрямую (более надежно)
      const inRange = itemDateStr >= startDateStr && itemDateStr <= endDateStr;
      
      if (!inRange && items.length > 0 && items.length <= 20) {
        // Логируем только для небольших массивов
        console.log('❌ [UserWorkTimeDetailsMobile] Элемент вне диапазона:', {
          itemDate: item[timestampField],
          itemDateStr,
          startDateStr,
          endDateStr,
          procName: item.procName || item.url
        });
      }
      return inRange;
    });
    
    console.log('✅ [UserWorkTimeDetailsMobile] filterByDateRange результат:', {
      before: items?.length || 0,
      after: filtered?.length || 0,
      timestampField
    });
    
    return filtered;
  }, [startDate, endDate]);

  // УДАЛЕН ДУБЛИКАТ ЛОГИРОВАНИЯ V3.0 - используем только V4.0 выше
  
  useEffect(() => {
    console.log('🔍 [UserWorkTimeDetailsMobile] Props applications:', applications?.length || 0, applications);
    console.log('🔍 [UserWorkTimeDetailsMobile] localApplications:', localApplications?.length || 0, localApplications);
    console.log('🔍 [UserWorkTimeDetailsMobile] localUrls:', localUrls?.length || 0);
    console.log('🔍 [UserWorkTimeDetailsMobile] localScreenshots:', localScreenshots?.length || 0);
  }, [applications, localApplications, localUrls, localScreenshots]);

  // Обработчики свайпа
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!ENABLE_MODAL_SWIPE_BACK) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      if (onOpenMobileSidebar) {
        onOpenMobileSidebar();
      }
      onClose();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleClose = () => {
    if (onOpenMobileSidebar) {
      onOpenMobileSidebar();
    }
    onClose();
  };

  // Обновляем локальное состояние при изменении props и сразу фильтруем по дате
  useEffect(() => {
    console.log('🔄 [UserWorkTimeDetailsMobile] Обновление локального состояния:', {
      urlsCount: urls?.length || 0,
      applicationsCount: applications?.length || 0,
      screenshotsCount: screenshots?.length || 0,
      hasActivityStats: !!activityStats,
      startDate,
      endDate
    });
    
    // Фильтруем данные сразу при установке из props
    const filteredUrls = filterByDateRange(urls || [], 'timestamp');
    const filteredApplications = filterByDateRange(applications || [], 'timestamp');
    const filteredScreenshots = filterByDateRange(screenshots || [], 'timestamp');
    
    console.log('🔍 [UserWorkTimeDetailsMobile] Отфильтрованные данные из props:', {
      urls: { before: urls?.length || 0, after: filteredUrls?.length || 0 },
      applications: { before: applications?.length || 0, after: filteredApplications?.length || 0 },
      screenshots: { before: screenshots?.length || 0, after: filteredScreenshots?.length || 0 }
    });
    
    setLocalUrls(filteredUrls);
    
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Всегда используем applications из props, если они переданы
    // Если applications передан (даже пустой массив), значит данные были загружены - используем его
    if (applications !== undefined && applications !== null) {
      // applications явно передан в props (даже если пустой массив)
      console.log('✅ [UserWorkTimeDetailsMobile] Используем отфильтрованные applications из props:', filteredApplications.length);
      if (filteredApplications.length > 0) {
        console.log('✅ [UserWorkTimeDetailsMobile] Первые 3 приложения:', filteredApplications.slice(0, 3).map(a => a.procName || a.name));
      }
      setLocalApplications(filteredApplications);
    } else if (activityStats && activityStats.topApps && activityStats.topApps.length > 0) {
      // Только если applications НЕ передан вообще (undefined), используем topApps для обратной совместимости
      console.log('⚠️ [UserWorkTimeDetailsMobile] applications не передан в props, используем topApps для обратной совместимости:', activityStats.topApps.length);
      const apps = activityStats.topApps.map(app => ({
        procName: app.name,
        windowTitle: app.name,
        timestamp: null,
        browserUrl: '',
      }));
      setLocalApplications(apps);
    } else {
      console.log('ℹ️ [UserWorkTimeDetailsMobile] Нет данных для applications, оставляем текущее состояние');
      // НЕ перезаписываем, если данных нет
    }
    
    if (screenshots !== undefined && screenshots !== null) {
      console.log('✅ [UserWorkTimeDetailsMobile] Устанавливаем отфильтрованные screenshots из props:', filteredScreenshots.length);
      if (filteredScreenshots.length > 0) {
        console.log('📸 [UserWorkTimeDetailsMobile] Первые 3 скриншота из props:', filteredScreenshots.slice(0, 3).map(s => ({
          id: s.id,
          url: s.url,
          filePath: s.filePath,
          timestamp: s.timestamp
        })));
      }
      setLocalScreenshots(filteredScreenshots);
    } else {
      console.log('ℹ️ [UserWorkTimeDetailsMobile] screenshots не передан в props, оставляем текущее состояние');
    }
    setLocalActivityStats(activityStats);
  }, [urls, applications, screenshots, activityStats, filterByDateRange, startDate, endDate]);

  // Функция для перезагрузки данных активности (мемоизация)
  const reloadActivityData = useCallback(async () => {
    // Используем realUsername для API запросов, если доступен, иначе username
    const apiUsername = realUsername || username;
    if (!apiUsername || !startDate || !endDate) {
      console.warn('⚠️ [UserWorkTimeDetailsMobile] reloadActivityData: недостаточно параметров', { apiUsername, startDate, endDate, realUsername, username });
      return;
    }
    
    console.log('🔄 [UserWorkTimeDetailsMobile] reloadActivityData вызывается с username:', apiUsername, 'realUsername:', realUsername);
    
    try {
      // Используем точные даты без расширения диапазона
      // Сервер уже правильно фильтрует данные, а мы дополнительно фильтруем на клиенте
      const params = new URLSearchParams({
        username: apiUsername, // Используем правильный username без split
        start: startDate,
        end: endDate,
      });
      
      console.log('📡 [UserWorkTimeDetailsMobile] Запрос данных с точными датами:', {
        start: startDate,
        end: endDate
      });
      const apiUrl = `/api/activity-details?${params.toString()}`;
      console.log('📡 [UserWorkTimeDetailsMobile] Запрос к API:', apiUrl);
      const res = await fetch(apiUrl);
      const data = await res.json();
      
      console.log('📡 [UserWorkTimeDetailsMobile] Ответ от API:', {
        status: res.status,
        success: data.success,
        urlsCount: data.urls?.length || 0,
        applicationsCount: data.applications?.length || 0,
        screenshotsCount: data.screenshots?.length || 0,
        applications: data.applications?.slice(0, 5),
        screenshots: data.screenshots?.slice(0, 3) // Показываем первые 3 скриншота для отладки
      });
      
      if (res.ok && data.success) {
        console.log('✅ [UserWorkTimeDetailsMobile] Данные получены от API:', {
          urlsCount: data.urls?.length || 0,
          applicationsCount: data.applications?.length || 0,
          applications: data.applications,
          screenshotsCount: data.screenshots?.length || 0,
          screenshots: data.screenshots
        });
        
        // Детальное логирование скриншотов
        if (data.screenshots && data.screenshots.length > 0) {
          console.log('📸 [UserWorkTimeDetailsMobile] Скриншоты из API:', data.screenshots.length);
          data.screenshots.forEach((shot, idx) => {
            if (idx < 3) { // Логируем только первые 3 для краткости
              console.log(`📸 [${idx}] Screenshot from API:`, {
                id: shot.id,
                url: shot.url,
                filePath: shot.filePath,
                timestamp: shot.timestamp,
                fileSize: shot.fileSize
              });
            }
          });
        } else {
          console.warn('⚠️ [UserWorkTimeDetailsMobile] Скриншоты отсутствуют в ответе API');
        }
        
        // Фильтруем данные по дате перед установкой в состояние
        const filteredApiUrls = filterByDateRange(data.urls || [], 'timestamp');
        const filteredApiApplications = filterByDateRange(data.applications || [], 'timestamp');
        const filteredApiScreenshots = filterByDateRange(data.screenshots || [], 'timestamp');
        
        console.log('🔍 [UserWorkTimeDetailsMobile] Отфильтрованные данные из API:', {
          urls: { before: data.urls?.length || 0, after: filteredApiUrls?.length || 0 },
          applications: { before: data.applications?.length || 0, after: filteredApiApplications?.length || 0 },
          screenshots: { before: data.screenshots?.length || 0, after: filteredApiScreenshots?.length || 0 }
        });
        
        if (data.urls) setLocalUrls(filteredApiUrls);
        if (data.applications) {
          console.log('✅ [UserWorkTimeDetailsMobile] Устанавливаем отфильтрованные applications:', filteredApiApplications.length, filteredApiApplications.slice(0, 3));
          setLocalApplications(filteredApiApplications);
        } else {
          console.warn('⚠️ [UserWorkTimeDetailsMobile] applications отсутствуют в ответе API');
          setLocalApplications([]);
        }
        if (data.screenshots) {
          console.log('✅ [UserWorkTimeDetailsMobile] Устанавливаем отфильтрованные screenshots через reloadActivityData:', filteredApiScreenshots.length);
          console.log('📸 [UserWorkTimeDetailsMobile] Первые 3 скриншота перед установкой:', filteredApiScreenshots.slice(0, 3).map(s => ({ id: s.id, url: s.url })));
          setLocalScreenshots(filteredApiScreenshots);
        } else {
          console.warn('⚠️ [UserWorkTimeDetailsMobile] screenshots отсутствуют в ответе API, устанавливаем пустой массив');
          setLocalScreenshots([]);
        }
        if (data.activityStats) setLocalActivityStats(data.activityStats);
      } else {
        console.error('❌ [UserWorkTimeDetailsMobile] Ошибка ответа API:', data);
      }
    } catch (err) {
      console.error('❌ [UserWorkTimeDetailsMobile] Error reloading activity data:', err);
    }
  }, [realUsername, username, startDate, endDate, filterByDateRange]);

  // WebSocket для обновления данных в реальном времени (используем общий socket)
  useEffect(() => {
    if (!open || !username || !startDate || !endDate) return;

    // Используем общий socket из SocketProvider вместо создания нового
    const socket = window.socket;
    if (!socket || !socket.connected) return;

    // Слушаем обновления данных активности
    const handleActivityUpdate = async (updateData) => {
      // Проверяем, относится ли обновление к текущему пользователю и дате
      const updateDate = updateData.date || (updateData.timestamp ? new Date(updateData.timestamp).toISOString().split('T')[0] : null);
      const usernameForMatch = username.split(' ')[0] || username;
      const matchesUser = updateData.username === username || updateData.username === usernameForMatch;
      const matchesDate = updateDate && updateDate >= startDate && updateDate <= endDate;
      
      if (matchesUser && matchesDate) {
        // Перезагружаем данные активности
        reloadActivityData();
      }
    };

    // Слушаем добавление новых скриншотов
    const handleScreenshotAdded = (screenshotData) => {
      // Проверяем, относится ли скриншот к текущему пользователю и дате
      const screenshotDate = screenshotData.date || (screenshotData.timestamp ? new Date(screenshotData.timestamp).toISOString().split('T')[0] : null);
      const usernameForMatch = username.split(' ')[0] || username;
      const matchesUser = screenshotData.username === username || screenshotData.username === usernameForMatch;
      const matchesDate = screenshotDate && screenshotDate >= startDate && screenshotDate <= endDate;
      
      if (matchesUser && matchesDate) {
        // Добавляем новый скриншот в список
        setLocalScreenshots(prev => {
          // Проверяем, нет ли уже такого скриншота
          const exists = prev.some(s => s.fileName === screenshotData.fileName || s.url === screenshotData.url);
          if (exists) return prev;
          
          return [...prev, {
            file_path: screenshotData.filePath,
            fileName: screenshotData.fileName,
            url: screenshotData.url,
            timestamp: screenshotData.timestamp,
            file_size: screenshotData.fileSize
          }].sort((a, b) => {
            const timeA = new Date(a.timestamp || 0).getTime();
            const timeB = new Date(b.timestamp || 0).getTime();
            return timeB - timeA; // Сортируем по убыванию (новые сверху)
          });
        });
      }
    };

    socket.on('activity_data_updated', handleActivityUpdate);
    socket.on('activity_screenshot_added', handleScreenshotAdded);

    return () => {
      if (socket) {
        socket.off('activity_data_updated', handleActivityUpdate);
        socket.off('activity_screenshot_added', handleScreenshotAdded);
      }
    };
  }, [open, username, startDate, endDate, reloadActivityData]);

  // Загружаем данные активности при открытии модалки
  useEffect(() => {
    if (open && realUsername && startDate && endDate) {
      console.log('🔄 [UserWorkTimeDetailsMobile] Модалка открыта, загружаем данные активности для:', realUsername, startDate, endDate);
      console.log('🔄 [UserWorkTimeDetailsMobile] Текущее состояние localScreenshots перед загрузкой:', localScreenshots?.length || 0);
      console.log('🔄 [UserWorkTimeDetailsMobile] Скриншоты из props:', screenshots?.length || 0);
      // Всегда обновляем данные через API, чтобы не показывать нули при неполных props
      reloadActivityData();
      // Если скриншоты пришли в props — ставим сразу, пока идет загрузка
      if (screenshots && screenshots.length > 0) {
        setLocalScreenshots(screenshots);
      }
    } else {
      console.log('⚠️ [UserWorkTimeDetailsMobile] Не загружаем данные - недостаточно параметров:', {
        open,
        realUsername,
        startDate,
        endDate
      });
    }
  }, [open, realUsername, startDate, endDate, reloadActivityData, screenshots]);

  // Фильтруем и сортируем логи по времени (мемоизация для оптимизации)
  // ВАЖНО: хуки должны вызываться ДО условного возврата
  const sortedLogs = useMemo(() => {
    if (!logs || logs.length === 0) return [];
    const filtered = filterByDateRange(logs, 'event_time');
    return filtered.sort((a, b) => {
      const timeA = new Date(a.event_time || 0).getTime();
      const timeB = new Date(b.event_time || 0).getTime();
      return timeA - timeB;
    });
  }, [logs, filterByDateRange]);

  // Фильтруем и сортируем скриншоты по времени (от новых к старым, как приложения и сайты)
  const sortedScreenshots = useMemo(() => {
    if (!localScreenshots || localScreenshots.length === 0) return [];
    const filtered = filterByDateRange(localScreenshots, 'timestamp');
    return filtered.sort((a, b) => {
      const timeA = new Date(a.timestamp || 0).getTime();
      const timeB = new Date(b.timestamp || 0).getTime();
      return timeB - timeA; // От новых к старым
    });
  }, [localScreenshots, filterByDateRange]);

  // Фильтруем URL-адреса по дате
  const filteredUrls = useMemo(() => {
    console.log('🔍 [UserWorkTimeDetailsMobile] Фильтрация URLs:', {
      localUrlsCount: localUrls?.length || 0,
      startDate,
      endDate
    });
    if (!localUrls || localUrls.length === 0) {
      console.log('⚠️ [UserWorkTimeDetailsMobile] localUrls пустой');
      return [];
    }
    const filtered = filterByDateRange(localUrls, 'timestamp');
    console.log('✅ [UserWorkTimeDetailsMobile] filteredUrls результат:', {
      before: localUrls.length,
      after: filtered.length
    });
    return filtered;
  }, [localUrls, filterByDateRange, startDate, endDate]);

  // Фильтруем приложения по дате
  const filteredApplications = useMemo(() => {
    console.log('🔍 [UserWorkTimeDetailsMobile] Фильтрация Applications:', {
      localApplicationsCount: localApplications?.length || 0,
      startDate,
      endDate
    });
    if (!localApplications || localApplications.length === 0) {
      console.log('⚠️ [UserWorkTimeDetailsMobile] localApplications пустой');
      return [];
    }
    const filtered = filterByDateRange(localApplications, 'timestamp');
    console.log('✅ [UserWorkTimeDetailsMobile] filteredApplications результат:', {
      before: localApplications.length,
      after: filtered.length,
      firstFew: filtered.slice(0, 3).map(a => ({ procName: a.procName, timestamp: a.timestamp }))
    });
    return filtered;
  }, [localApplications, filterByDateRange, startDate, endDate]);

  // Переработка: минуты вне 9:00–18:00 (та же логика, что в колонке «Отработано» таблицы)
  const getOvertimeMinutes = useCallback((firstLogin, lastLogout, workStartHour = 9, workEndHour = 18) => {
    if (!firstLogin || !lastLogout) return 0;
    const start = new Date(firstLogin);
    const end = new Date(lastLogout);
    if (end <= start) return 0;
    let overtime = 0;
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= endDay) {
      const workStart = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workStartHour, 0, 0, 0);
      const workEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), workEndHour, 0, 0, 0);
      const dayEnd = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), 23, 59, 59, 999);
      const sessionOnDayStart = start > cur ? start : cur;
      const sessionOnDayEnd = end < dayEnd ? end : dayEnd;
      if (sessionOnDayStart < sessionOnDayEnd) {
        if (sessionOnDayStart < workStart) {
          const earlyEnd = workStart < sessionOnDayEnd ? workStart : sessionOnDayEnd;
          overtime += (earlyEnd.getTime() - sessionOnDayStart.getTime()) / 60000;
        }
        if (sessionOnDayEnd > workEnd) {
          const lateStart = workEnd > sessionOnDayStart ? workEnd : sessionOnDayStart;
          overtime += (sessionOnDayEnd.getTime() - lateStart.getTime()) / 60000;
        }
      }
      cur.setDate(cur.getDate() + 1);
    }
    return Math.round(overtime);
  }, []);

  // Отработано по приложениям (первый — последний вход): совпадает с колонкой «Отработано» в таблице
  const workedFromApplications = useMemo(() => {
    if (!filteredApplications || filteredApplications.length === 0) return null;
    const withTs = filteredApplications
      .map(a => ({ ...a, t: new Date(a.timestamp).getTime() }))
      .filter(a => !isNaN(a.t));
    if (withTs.length === 0) return null;
    const first = Math.min(...withTs.map(a => a.t));
    const last = Math.max(...withTs.map(a => a.t));
    const workedMinutes = Math.round((last - first) / 60000);
    const overtimeMinutes = getOvertimeMinutes(first, last, 9, 18);
    return {
      firstAppTime: first,
      lastAppTime: last,
      workedMinutes,
      overtimeMinutes,
      workedHours: Math.floor(workedMinutes / 60),
      workedMins: workedMinutes % 60,
      overtimeHours: Math.floor(overtimeMinutes / 60),
      overtimeMins: overtimeMinutes % 60,
    };
  }, [filteredApplications, getOvertimeMinutes]);

  // Функция для скачивания скриншота
  const handleDownloadScreenshot = useCallback((screenshotUrl, timestamp) => {
    const dateStr = timestamp ? formatTime(timestamp).replace(/[^\w\s]/gi, '_') : 'screenshot';
    const fullUrl = screenshotUrl.startsWith('http') ? screenshotUrl : `${window.location.origin}${screenshotUrl}`;
    
    // Создаем временную ссылку для скачивания
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = `screenshot_${dateStr}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Функция для печати отдельного скриншота
  const handlePrintScreenshot = useCallback((screenshotUrl, timestamp) => {
    const dateStr = timestamp ? formatTime(timestamp) : 'Неизвестная дата';
    const fullUrl = screenshotUrl.startsWith('http') ? screenshotUrl : `${window.location.origin}${screenshotUrl}`;
    
    // Определяем, мобильное ли устройство
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Для всех устройств создаем страницу с кнопками управления
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Пожалуйста, разрешите всплывающие окна для печати');
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Скриншот - ${dateStr}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            @media print {
              body { margin: 0; padding: 10px; }
              .header { margin-bottom: 15px; text-align: center; }
              .screenshot { max-width: 100%; height: auto; page-break-inside: avoid; }
              .controls { display: none !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px; 
              margin: 0;
              background: #fff;
            }
            .header { 
              margin-bottom: 20px; 
              text-align: center; 
              border-bottom: 2px solid #333; 
              padding-bottom: 10px; 
            }
            .screenshot { 
              max-width: 100%; 
              height: auto; 
              border: 1px solid #ddd; 
              display: block;
              margin: 20px auto;
            }
            .timestamp { 
              text-align: center; 
              margin-top: 10px; 
              color: #666; 
              font-size: 12px;
            }
            .loading {
              text-align: center;
              padding: 40px;
              color: #666;
            }
            .controls {
              text-align: center;
              margin: 20px 0;
              padding: 15px;
              background: #f5f5f5;
              border-radius: 8px;
            }
            .controls button {
              padding: 12px 24px;
              margin: 5px;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.2s;
            }
            .btn-print {
              background: #2193b0;
              color: white;
            }
            .btn-print:hover {
              background: #1a7a91;
            }
            .btn-download {
              background: #43e97b;
              color: white;
            }
            .btn-download:hover {
              background: #35c765;
            }
            .btn-close {
              background: #e74c3c;
              color: white;
            }
            .btn-close:hover {
              background: #c0392b;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 style="margin: 0; font-size: 18px;">Скриншот активности</h1>
            <p style="margin: 5px 0; font-size: 14px;">Пользователь: ${username}</p>
            <p style="margin: 5px 0; font-size: 12px;">Дата и время: ${dateStr}</p>
          </div>
          
          <div class="controls">
            <button class="btn-print" onclick="window.print();">🖨️ Печать / Сохранить в PDF</button>
            <button class="btn-download" onclick="downloadImage();">💾 Скачать изображение</button>
            <button class="btn-close" onclick="window.close();">✕ Закрыть</button>
          </div>
          
          <div class="loading" id="loading">Загрузка изображения...</div>
          <img 
            id="screenshot-img"
            src="${fullUrl}" 
            alt="Скриншот" 
            class="screenshot" 
            style="display: none;"
            onload="
              document.getElementById('loading').style.display = 'none';
              this.style.display = 'block';
            "
            onerror="
              document.getElementById('loading').innerHTML = 'Ошибка загрузки изображения.<br/>Попробуйте открыть изображение напрямую.';
              document.getElementById('loading').style.color = '#e74c3c';
            "
          />
          <div class="timestamp">${dateStr}</div>
          
          <script>
            function downloadImage() {
              const img = document.getElementById('screenshot-img');
              const link = document.createElement('a');
              link.href = img.src;
              link.download = 'screenshot_${dateStr.replace(/[^\\w\\s]/gi, '_')}.jpg';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }, [username]);

  // Функция для печати полного отчета
  const handlePrintReport = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Пожалуйста, разрешите всплывающие окна для печати');
      return;
    }
    
    const dateRange = `${startDate} - ${endDate}`;
    let reportContent = '';
    
    // Заголовок
    reportContent += `
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #333; padding-bottom: 20px;">
        <h1 style="margin: 0; color: #333;">Отчет активности</h1>
        <p style="margin: 10px 0; color: #666; font-size: 16px;">Пользователь: ${username}</p>
        <p style="margin: 5px 0; color: #666; font-size: 14px;">Период: ${dateRange}</p>
        <p style="margin: 5px 0; color: #666; font-size: 12px;">Дата формирования: ${new Date().toLocaleString('ru-RU')}</p>
      </div>
    `;
    
    // События (входы/выходы)
    if (printOptions.events && sortedLogs && sortedLogs.length > 0) {
      reportContent += `
        <div style="page-break-inside: avoid; margin-bottom: 30px;">
          <h2 style="color: #333; border-bottom: 2px solid #43e97b; padding-bottom: 10px;">События (${sortedLogs.length})</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <thead>
              <tr style="background:rgb(255, 255, 255);">
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Дата и время</th>
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Тип события</th>
              </tr>
            </thead>
            <tbody>
      `;
      sortedLogs.forEach(log => {
        const eventType = log.event_type === 'login' ? 'Вход' : log.event_type === 'logout' ? 'Выход' : 'Другое';
        const eventColor = log.event_type === 'login' ? '#43e97b' : log.event_type === 'logout' ? '#e74c3c' : '#666';
        reportContent += `
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd;">${formatTime(log.event_time)}</td>
            <td style="padding: 8px; border: 1px solid #ddd; color: ${eventColor}; font-weight: 600;">${eventType}</td>
          </tr>
        `;
      });
      reportContent += `</tbody></table></div>`;
    }
    
    // Приложения
    if (printOptions.applications && filteredApplications && filteredApplications.length > 0) {
      reportContent += `
        <div style="page-break-inside: avoid; margin-bottom: 30px;">
          <h2 style="color: #333; border-bottom: 2px solid #43e97b; padding-bottom: 10px;">Приложения (${filteredApplications.length})</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Приложение</th>
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Окно</th>
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Время</th>
              </tr>
            </thead>
            <tbody>
      `;
      filteredApplications.slice(0, 100).forEach(app => {
        reportContent += `
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; font-weight: 600;">${app.procName || 'Неизвестно'}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${(app.windowTitle || '').substring(0, 50)}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${app.timestamp ? formatTime(app.timestamp) : '-'}</td>
          </tr>
        `;
      });
      if (filteredApplications.length > 100) {
        reportContent += `
          <tr>
            <td colspan="3" style="padding: 8px; border: 1px solid #ddd; text-align: center; color: #666;">
              ... и еще ${filteredApplications.length - 100} приложений
            </td>
          </tr>
        `;
      }
      reportContent += `</tbody></table></div>`;
    }
    
    // Сайты
    if (printOptions.urls && filteredUrls && filteredUrls.length > 0) {
      reportContent += `
        <div style="page-break-inside: avoid; margin-bottom: 30px;">
          <h2 style="color: #333; border-bottom: 2px solid #2193b0; padding-bottom: 10px;">Сайты (${filteredUrls.length})</h2>
          <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">URL</th>
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Время</th>
              </tr>
            </thead>
            <tbody>
      `;
      filteredUrls.slice(0, 100).forEach(url => {
        reportContent += `
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd; word-break: break-all;">${url.url || '-'}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${url.timestamp ? formatTime(url.timestamp) : '-'}</td>
          </tr>
        `;
      });
      if (filteredUrls.length > 100) {
        reportContent += `
          <tr>
            <td colspan="2" style="padding: 8px; border: 1px solid #ddd; text-align: center; color: #666;">
              ... и еще ${filteredUrls.length - 100} сайтов
            </td>
          </tr>
        `;
      }
      reportContent += `</tbody></table></div>`;
    }
    
    // Скриншоты
    if (printOptions.screenshots && sortedScreenshots && sortedScreenshots.length > 0) {
      reportContent += `
        <div style="page-break-inside: avoid; margin-bottom: 30px;">
          <h2 style="color: #333; border-bottom: 2px solid #ffe082; padding-bottom: 10px;">Скриншоты (${sortedScreenshots.length})</h2>
      `;
      sortedScreenshots.forEach((shot, idx) => {
        const screenshotUrl = shot.url && shot.url.startsWith('/') 
          ? shot.url 
          : shot.url && shot.url.startsWith('http')
          ? shot.url
          : `/uploads/screenshots/${shot.filePath ? shot.filePath.split(/[\/\\]/).pop() : shot.url || `screenshot_${idx}.jpg`}`;
        const fullUrl = screenshotUrl.startsWith('http') ? screenshotUrl : `${window.location.origin}${screenshotUrl}`;
        
        reportContent += `
          <div style="page-break-inside: avoid; margin-bottom: 20px; border: 1px solid #ddd; padding: 15px;">
            <p style="margin: 0 0 10px 0; color: #666; font-size: 12px;">Скриншот ${idx + 1} - ${formatTime(shot.timestamp)}</p>
            <img src="${fullUrl}" alt="Скриншот ${idx + 1}" style="max-width: 100%; height: auto; border: 1px solid #ddd;" />
          </div>
        `;
      });
      reportContent += `</div>`;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Отчет активности - ${username}</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
              .page-break { page-break-before: always; }
            }
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h1, h2 { color: #333; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 10px; text-align: left; border: 1px solid #ddd; }
            th { background: #f5f5f5; font-weight: 600; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          ${reportContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Ждем загрузки изображений перед печатью
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [username, startDate, endDate, sortedLogs, filteredApplications, filteredUrls, sortedScreenshots, printOptions]);

  // Добавляем эффект для принудительного применения стилей модалки
  useEffect(() => {
    if (!open) return;

    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const styleId = 'user-worktime-details-modal-styles';
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      [data-user-worktime-details-modal] {
        ${isMobile ? 'left: 0 !important; width: 100vw !important;' : 'left: 320px !important; width: calc(100vw - 320px) !important;'}
        right: 0 !important;
      }
      [data-user-worktime-details-modal] > div {
        border-radius: 0 !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);

    // Прямая DOM-манипуляция
    const applyStyles = () => {
      const overlay = document.querySelector('[data-user-worktime-details-modal]');
      if (overlay) {
        const s = overlay.style;
        if (isMobile) {
          s.setProperty('left', '0', 'important');
          s.setProperty('width', '100vw', 'important');
        } else {
          s.setProperty('left', '320px', 'important');
          s.setProperty('width', 'calc(100vw - 320px)', 'important');
        }
        s.setProperty('right', '0', 'important');
        s.setProperty('align-items', 'stretch', 'important');
        s.setProperty('justify-content', 'flex-start', 'important');
        
        // Исправляем заголовок (первый div с position: fixed внутри overlay)
        const headers = overlay.querySelectorAll('div[style*="position: fixed"]');
        headers.forEach(header => {
          const hs = header.style;
          hs.setProperty('left', '0', 'important');
          hs.setProperty('right', '0', 'important');
          hs.setProperty('width', '100%', 'important');
          hs.setProperty('max-width', '100%', 'important');
          hs.setProperty('box-sizing', 'border-box', 'important');
        });
      }
      const content = overlay?.querySelector('div');
      if (content) {
        const cs = content.style;
        cs.setProperty('border-radius', '0', 'important');
        cs.setProperty('box-shadow', 'none', 'important');
        cs.setProperty('width', '100%', 'important');
        cs.setProperty('max-width', '100%', 'important');
        cs.setProperty('box-sizing', 'border-box', 'important');
      }
    };

    setTimeout(applyStyles, 0);
    setTimeout(applyStyles, 10);
    setTimeout(applyStyles, 50);
    setTimeout(applyStyles, 100);
    setTimeout(applyStyles, 200);

    const observer = new MutationObserver(applyStyles);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
      observer.disconnect();
    };
  }, [open]);

  // Не рендерим модалку, если она закрыта
  if (!open) {
    return null;
  }

  const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const modalVars = {
    background: 'var(--modal-local-pc-activity-details-background, #ffffff)',
    backgroundImage: 'var(--modal-local-pc-activity-details-background-image, none)',
    backgroundImageOpacity: 'var(--modal-local-pc-activity-details-background-image-opacity, 1)'
  };
  const hasBackgroundImage = modalVars.backgroundImage && modalVars.backgroundImage !== 'none';

  return ReactDOM.createPortal(
    <div
      data-user-worktime-details-modal
      style={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : '320px',
        right: 0,
        bottom: 0,
        width: isMobile ? '100vw' : 'calc(100vw - 320px)',
        background: modalVars.background,
        ...(hasBackgroundImage
          ? {
              backgroundImage: modalVars.backgroundImage,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }
          : {}),
        zIndex: 100004, // Выше LocalWorktimeReportModalMobile (100003) и WorkTimeMobile (100002)
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        margin: 0,
        padding: 0,
      }}
      onClick={handleClose}
    >
      {hasBackgroundImage && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: modalVars.background,
          opacity: `calc(1 - ${modalVars.backgroundImageOpacity})`,
          pointerEvents: 'none'
        }} />
      )}
      <div
        ref={modalRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '56px',
          paddingBottom: '20px',
          boxSizing: 'border-box',
          borderRadius: 0,
          boxShadow: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
        {...(ENABLE_MODAL_SWIPE_BACK ? { onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd } : {})}
      >
        {/* Header */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            width: '100%',
            maxWidth: '100%',
            background: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            zIndex: 20001,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            boxSizing: 'border-box',
          }}
        >
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
            }}
            title="Назад"
          >
            <FaArrowLeft />
          </button>
          
          <h2
            style={{
              color: '#fff',
              fontSize: '19px',
              fontWeight: 700,
              margin: 0,
              marginLeft: 330,
              flex: 1,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
            }}
          >
            <FiClock size={18} />
            Подробности: {toCyrillicFio(username)}
          </h2>

          <button
            onClick={() => setShowPrintDialog(true)}
            style={{
              background: 'rgba(255, 224, 130, 0.2)',
              border: '1px solid rgba(255, 224, 130, 0.5)',
              color: '#ffe082',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              fontWeight: 600,
              marginRight: '8px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 224, 130, 0.3)';
              e.target.style.borderColor = '#ffe082';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 224, 130, 0.2)';
              e.target.style.borderColor = 'rgba(255, 224, 130, 0.5)';
            }}
            title="Печать отчета"
          >
            <FiPrinter size={16} />
            Печать
          </button>

          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
            }}
            title="Закрыть"
          >
            <FiX />
          </button>
        </div>

        {/* Табы */}
        {(() => {
          console.log('📑 [UserWorkTimeDetailsMobile] Рендер секции табов, activeTab:', activeTab, 'localApplications count:', localApplications?.length || 0, 'localApplications:', localApplications);
          return (
        <div style={{
          position: 'sticky',
          top: '0px',
          background: 'var(--modal-local-pc-activity-details-tab-bar-background, linear-gradient(135deg, #232931 0%, #181c22 100%))',
          zIndex: 20000,
          padding: '12px 16px',
          paddingBottom: '12px',
          borderBottom: '1px solid var(--modal-local-pc-activity-details-tab-border-color, rgba(255, 255, 255, 0.1))',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          marginTop: '8px',
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleTabChange('events')}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '8px',
                border: activeTab === 'events'
                  ? '2px solid var(--modal-local-pc-activity-details-tab-active-border-color, #000)'
                  : '1px solid var(--modal-local-pc-activity-details-tab-border-color, transparent)',
                background: activeTab === 'events' 
                  ? 'var(--modal-local-pc-activity-details-tab-active-background, #fff)' 
                  : 'var(--modal-local-pc-activity-details-tab-background, rgba(255, 255, 255, 0.1))',
                color: activeTab === 'events'
                  ? 'var(--modal-local-pc-activity-details-tab-active-text-color, #000)'
                  : 'var(--modal-local-pc-activity-details-tab-text-color, #fff)',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
              title="События"
            >
              <FiClock size={18} />
              <span style={{ fontSize: '11px', fontWeight: 700 }}>{sortedLogs?.length || 0}</span>
            </button>
            <button
              onClick={() => handleTabChange('urls')}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '8px',
                border: activeTab === 'urls'
                  ? '2px solid var(--modal-local-pc-activity-details-tab-active-border-color, #000)'
                  : '1px solid var(--modal-local-pc-activity-details-tab-border-color, transparent)',
                background: activeTab === 'urls' 
                  ? 'var(--modal-local-pc-activity-details-tab-active-background, #fff)' 
                  : 'var(--modal-local-pc-activity-details-tab-background, rgba(255, 255, 255, 0.1))',
                color: activeTab === 'urls'
                  ? 'var(--modal-local-pc-activity-details-tab-active-text-color, #000)'
                  : 'var(--modal-local-pc-activity-details-tab-text-color, #fff)',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
              title="Сайты"
            >
              <FiGlobe size={18} />
              <span style={{ fontSize: '11px', fontWeight: 700 }}>{filteredUrls?.length || 0}</span>
            </button>
            <button
              onClick={() => {
                console.log('🖱️ [UserWorkTimeDetailsMobile] Клик по вкладке Приложения, filteredApplications:', filteredApplications?.length || 0, filteredApplications);
                handleTabChange('applications');
              }}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '8px',
                border: activeTab === 'applications'
                  ? '2px solid var(--modal-local-pc-activity-details-tab-active-border-color, #000)'
                  : '1px solid var(--modal-local-pc-activity-details-tab-border-color, transparent)',
                background: activeTab === 'applications' 
                  ? 'var(--modal-local-pc-activity-details-tab-active-background, #fff)' 
                  : 'var(--modal-local-pc-activity-details-tab-background, rgba(255, 255, 255, 0.1))',
                color: activeTab === 'applications'
                  ? 'var(--modal-local-pc-activity-details-tab-active-text-color, #000)'
                  : 'var(--modal-local-pc-activity-details-tab-text-color, #fff)',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                zIndex: 10,
                position: 'relative',
              }}
              title="Приложения"
            >
              <FiMonitor size={18} />
              <span style={{ fontSize: '11px', fontWeight: 700 }}>{filteredApplications?.length || 0}</span>
            </button>
            <button
              onClick={() => handleTabChange('screenshots')}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '8px',
                border: activeTab === 'screenshots'
                  ? '2px solid var(--modal-local-pc-activity-details-tab-active-border-color, #000)'
                  : '1px solid var(--modal-local-pc-activity-details-tab-border-color, transparent)',
                background: activeTab === 'screenshots' 
                  ? 'var(--modal-local-pc-activity-details-tab-active-background, #fff)' 
                  : 'var(--modal-local-pc-activity-details-tab-background, rgba(255, 255, 255, 0.1))',
                color: activeTab === 'screenshots'
                  ? 'var(--modal-local-pc-activity-details-tab-active-text-color, #000)'
                  : 'var(--modal-local-pc-activity-details-tab-text-color, #fff)',
                fontWeight: 600,
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
              title="Скрины"
            >
              <FiCamera size={18} />
              <span style={{ fontSize: '11px', fontWeight: 700 }}>{localScreenshots?.length || 0}</span>
            </button>
          </div>
        </div>
          );
        })()}

        {/* Контент - добавляем отступ сверху, чтобы не перекрывался панелью вкладок */}
        <div style={{ 
          flex: 1, 
          padding: '24px 16px', 
          paddingTop: '16px', 
          maxWidth: '600px', 
          margin: '0 auto', 
          width: '100%',
          position: 'relative',
          marginTop: '0',
        }}>
          {/* Таб: События */}
          {activeTab === 'events' && (
            <>
              {/* Отработано по приложениям — совпадает с колонкой «Отработано» в таблице */}
              {workedFromApplications && (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'var(--modal-local-pc-activity-details-block-background, linear-gradient(135deg, rgba(255, 224, 130, 0.1) 0%, rgba(33, 147, 176, 0.1) 100%))',
                  border: '2px solid var(--modal-local-pc-activity-details-block-border-color, #000)',
                  marginBottom: '24px',
                }}>
                  <div style={{
                    color: headerColor,
                    fontSize: '16px',
                    fontWeight: 700,
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <FiClock size={20} />
                    Отработано (по приложениям — как в таблице)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{
                      padding: '12px',
                      borderRadius: '8px',
                      background: 'var(--modal-local-pc-activity-details-stats-card-background, #27ae60)',
                      border: '1px solid var(--modal-local-pc-activity-details-stats-card-border-color, #000000)',
                    }}>
                      <div style={{
                        color: headerColor,
                        fontSize: '13px',
                        fontWeight: 600,
                        marginBottom: '6px',
                      }}>
                        ⏱ Отработано
                      </div>
                      <div style={{
                        color: 'var(--modal-local-pc-activity-details-stats-card-value-color, #fff)',
                        fontSize: '18px',
                        fontWeight: 700,
                      }}>
                        {workedFromApplications.workedHours > 0 ? `${workedFromApplications.workedHours} ч ` : ''}{workedFromApplications.workedMins} мин
                      </div>
                      {workedFromApplications.overtimeMinutes > 0 && (
                        <div style={{
                          color: 'var(--modal-local-pc-activity-details-stats-card-subtext-color, rgba(0, 0, 0, 0.6))',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}>
                          Переработка: {workedFromApplications.overtimeHours > 0 ? `${workedFromApplications.overtimeHours} ч ` : ''}{workedFromApplications.overtimeMins} мин
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Статистика - отображается первой */}
              {((localActivityStats?.totalActiveMinutes !== undefined && localActivityStats?.totalIdleMinutes !== undefined) || (activityStats?.totalActiveMinutes !== undefined && activityStats?.totalIdleMinutes !== undefined)) ? (() => {
                const stats = localActivityStats || activityStats;
                console.log('📊 [UserWorkTimeDetailsMobile] Проверка отображения статистики:', {
                  localActivityStats,
                  activityStats,
                  statsUsed: stats,
                  totalActiveMinutes: stats?.totalActiveMinutes,
                  totalIdleMinutes: stats?.totalIdleMinutes,
                  activeDefined: stats?.totalActiveMinutes !== undefined && stats?.totalActiveMinutes !== null,
                  idleDefined: stats?.totalIdleMinutes !== undefined && stats?.totalIdleMinutes !== null
                });
                if (!stats) return null;

                const totalActiveMinutes = Math.round(stats.totalActiveMinutes || 0);
                const totalIdleMinutes = Math.round(stats.totalIdleMinutes || 0);
                const activeHours = Math.floor(totalActiveMinutes / 60);
                const activeMins = totalActiveMinutes % 60;
                const idleHours = Math.floor(totalIdleMinutes / 60);
                const idleMins = totalIdleMinutes % 60;
                const totalMinutes = totalActiveMinutes + totalIdleMinutes;
                const totalHours = Math.floor(totalMinutes / 60);
                const totalMins = totalMinutes % 60;

                return (
                  <div style={{
                    padding: '20px',
                    borderRadius: '12px',
                    background: 'var(--modal-local-pc-activity-details-block-background, linear-gradient(135deg, rgba(255, 224, 130, 0.1) 0%, rgba(33, 147, 176, 0.1) 100%))',
                    border: '2px solid var(--modal-local-pc-activity-details-block-border-color, #000)',
                    marginBottom: '24px',
                  }}>
                    <div style={{
                      color: headerColor,
                      fontSize: '16px',
                      fontWeight: 700,
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}>
                      <FiClock size={20} />
                      По логам активности (активно / простой)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Статистика активности и простоя - главная информация */}
                      <>
                        <div style={{
                          padding: '12px',
                          borderRadius: '8px',
                          background: 'var(--modal-local-pc-activity-details-stats-card-background, #27ae60)',
                          border: '1px solid var(--modal-local-pc-activity-details-stats-card-border-color, #000000)',
                        }}>
                          <div style={{
                            color: headerColor,
                            fontSize: '13px',
                            fontWeight: 600,
                            marginBottom: '6px',
                          }}>
                            ⏱ Время работы (активность)
                          </div>
                          <div style={{
                            color: 'var(--modal-local-pc-activity-details-stats-card-value-color, #fff)',
                            fontSize: '18px',
                            fontWeight: 700,
                          }}>
                            {activeHours > 0 ? `${activeHours} ч ` : ''}{activeMins} мин
                          </div>
                          <div style={{
                            color: 'var(--modal-local-pc-activity-details-stats-card-subtext-color, rgba(0, 0, 0, 0.6))',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}>
                            ({totalActiveMinutes} минут)
                          </div>
                        </div>

                        <div style={{
                          padding: '12px',
                          borderRadius: '8px',
                          background: 'var(--modal-local-pc-activity-details-stats-card-background, #e74c3c)',
                          border: '1px solid var(--modal-local-pc-activity-details-stats-card-border-color, #000000)',
                        }}>
                          <div style={{
                            color: headerColor,
                            fontSize: '13px',
                            fontWeight: 600,
                            marginBottom: '6px',
                          }}>
                            ⏸ Время простоя
                          </div>
                          <div style={{
                            color: 'var(--modal-local-pc-activity-details-stats-card-value-color, #fff)',
                            fontSize: '18px',
                            fontWeight: 700,
                          }}>
                            {idleHours > 0 ? `${idleHours} ч ` : ''}{idleMins} мин
                          </div>
                          <div style={{
                            color: 'var(--modal-local-pc-activity-details-stats-card-subtext-color, rgba(1, 0, 0, 0.6))',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}>
                            ({totalIdleMinutes} минут)
                          </div>
                        </div>

                        {totalMinutes > 0 && (
                          <div style={{
                            padding: '12px',
                            borderRadius: '8px',
                            background: 'var(--modal-local-pc-activity-details-stats-card-background, #656768)',
                            border: '1px solid var(--modal-local-pc-activity-details-stats-card-border-color, #080300)',
                          }}>
                            <div style={{
                              color: headerColor,
                              fontSize: '13px',
                              fontWeight: 600,
                              marginBottom: '6px',
                            }}>
                              📊 Всего времени
                            </div>
                            <div style={{
                              color: 'var(--modal-local-pc-activity-details-stats-card-value-color, #fff)',
                              fontSize: '16px',
                              fontWeight: 700,
                            }}>
                              {totalHours > 0 ? `${totalHours} ч ` : ''}{totalMins} мин
                            </div>
                          </div>
                        )}

                        {stats.topApps && stats.topApps.length > 0 && (
                          <div style={{
                            padding: '12px',
                            borderRadius: '8px',
                            background: 'var(--modal-local-pc-activity-details-top-apps-background, #3d3a3a)',
                            border: '1px solid var(--modal-local-pc-activity-details-top-apps-border-color, #000)',
                            marginTop: '4px',
                          }}>
                            <div style={{
                              color: headerColor,
                              fontSize: '13px',
                              fontWeight: 600,
                              marginBottom: '8px',
                            }}>
                              🔝 Топ приложений:
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--modal-local-pc-activity-details-top-apps-text-color, rgba(251, 198, 84, 0.94))' }}>
                              {stats.topApps
                                .map(app => `${app.name || 'unknown'} (${app.minutes} мин)`)
                                .join(', ')}
                            </div>
                          </div>
                        )}
                      </>
                    </div>
                  </div>
                );
              })() : (
                <div style={{
                  padding: '20px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  marginBottom: '24px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}>
                  Статистика работы загружается...
                </div>
              )}

              {/* Список событий */}
              {!sortedLogs || sortedLogs.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '15px',
                }}>
                  Нет данных о событиях
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {sortedLogs.map((log, idx) => {
                const isLogin = log.event_type === 'login' || log.event_type === 'other';
                return (
                  <div
                    key={idx}
                    style={{
                      background: 'var(--modal-local-pc-activity-details-event-card-background, rgba(67, 233, 123, 0.1))',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '2px solid var(--modal-local-pc-activity-details-event-card-border-color, rgba(67, 233, 123, 0.3))',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: isLogin
                        ? 'linear-gradient(135deg, #43e97b 0%, #2193b0 100%)'
                        : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '20px',
                      flexShrink: 0,
                    }}>
                      {isLogin ? <FiLogIn size={24} /> : <FiLogOut size={24} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        color: headerColor,
                        fontSize: '15px',
                        fontWeight: 700,
                        marginBottom: '4px',
                      }}>
                        {isLogin ? 'Вход' : 'Выход'}
                      </div>
                      <div style={{
                        color: 'var(--modal-local-pc-activity-details-event-text-color, #111)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        <FiClock size={14} />
                        {formatTime(log.event_time)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Статистика: рамка и фон чёрные, заголовок и подписи — чёрный текст, значения без изменений */}
          {sortedLogs && sortedLogs.length > 0 && (
            <div style={{
              marginTop: '24px',
              padding: '2px',
              borderRadius: '12px',
              background: '#000',
              border: '2px solid #000',
            }}>
              <div style={{
                padding: '14px',
                borderRadius: '10px',
                background: '#fff',
              }}>
                <div style={{
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '12px',
                }}>
                  Статистика
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: '#000' }}>Всего событий:</span>
                    <span style={{ color: '#fff', fontWeight: 600, background: '#333', padding: '2px 8px', borderRadius: '4px' }}>{sortedLogs.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: '#000' }}>Входов:</span>
                    <span style={{ color: '#43e97b', fontWeight: 600 }}>
                      {sortedLogs.filter(l => l.event_type === 'login' || l.event_type === 'other').length}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: '#000' }}>Выходов:</span>
                    <span style={{ color: '#e74c3c', fontWeight: 600 }}>
                      {sortedLogs.filter(l => l.event_type === 'logout').length}
                    </span>
                  </div>
                  {activityStats && (
                    <>
                      <div style={{ height: 1, background: 'rgba(0,0,0,0.1)', margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                        <span style={{ color: '#000' }}>Активность за период:</span>
                        <span style={{ fontWeight: 600 }}>
                          <span style={{ color: '#43e97b' }}>{activityStats.totalActiveMinutes} мин активно</span>
                          {' / '}
                          <span style={{ color: '#e74c3c' }}>{activityStats.totalIdleMinutes} мин простоя</span>
                        </span>
                      </div>
                      {activityStats.topApps && activityStats.topApps.length > 0 && (
                        <div style={{ fontSize: '13px', marginTop: '4px' }}>
                          <span style={{ color: '#000', fontWeight: 600 }}>Топ приложений:</span>
                          <div style={{
                            color: '#000',
                            background: 'rgba(0,0,0,0.06)',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            marginTop: '6px',
                            border: '1px solid rgba(0,0,0,0.12)',
                          }}>
                            {activityStats.topApps
                              .map(app => `${app.name || 'unknown'} (${app.minutes} мин)`)
                              .join(', ')}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
            </>
          )}

          {/* Таб: Сайты */}
          {activeTab === 'urls' && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
            }}>
              {/* Краткая статистика */}
              {localActivityStats && (
                <div style={{
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'var(--modal-local-pc-activity-details-period-block-background, #9edaf3af)',
                  border: '1px solid var(--modal-local-pc-activity-details-period-block-border-color, rgba(33, 147, 176, 0.3))',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    color: headerColor,
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}>
                    Активность за период
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--modal-local-pc-activity-details-period-text-color, hsl(205, 91%, 31%))' }}>
                    {localActivityStats.totalActiveMinutes} мин активно / {localActivityStats.totalIdleMinutes} мин простоя
                  </div>
                </div>
              )}
              {!filteredUrls || filteredUrls.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '15px',
                }}>
                  Нет данных об открытых сайтах
                </div>
              ) : (
                <div style={{ 
                  maxHeight: 'calc(100vh - 300px)', 
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}>
                  {filteredUrls.map((item, idx) => (
                    <div
                      key={`url-${item.timestamp || idx}-${item.url?.substring(0, 20) || idx}`}
                      style={{
                        background: 'var(--modal-local-pc-activity-details-url-card-background, #93cbe5)',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '2px solid var(--modal-local-pc-activity-details-url-card-border-color, #000)',
                        marginBottom: idx < filteredUrls.length - 1 ? '12px' : 0,
                        color: 'var(--modal-local-pc-activity-details-url-text-color, #f3ecec)',
                      }}
                    >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                    }}>
                      <FiGlobe size={20} color="#fafbfb" />
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: headerColor,
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {item.url.length > 50 ? item.url.substring(0, 50) + '...' : item.url}
                        <FiExternalLink size={14} />
                      </a>
                    </div>
                    {item.windowTitle && (
                      <div style={{
                        color: 'var(--modal-local-pc-activity-details-url-text-color, rgb(248, 247, 247))',
                        fontSize: '13px',
                        marginBottom: '8px',
                      }}>
                        {item.windowTitle}
                      </div>
                    )}
                    <div style={{
                      color: 'var(--modal-local-pc-activity-details-url-meta-text-color, rgba(252, 254, 255, 0.99))',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <FiClock size={12} />
                      {formatTime(item.timestamp)}
                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Таб: Приложения */}
          {activeTab === 'applications' && (() => {
            console.log('📱 [UserWorkTimeDetailsMobile] Рендер вкладки Приложения, activeTab:', activeTab, 'filteredApplications:', filteredApplications?.length || 0);
            console.log('📱 [UserWorkTimeDetailsMobile] ВСЕ filteredApplications:', filteredApplications);
            if (filteredApplications && filteredApplications.length > 0) {
              console.log('📱 [UserWorkTimeDetailsMobile] Всего приложений для отображения:', filteredApplications.length);
              filteredApplications.forEach((app, idx) => {
                console.log(`  [${idx}] ${app.procName || 'NO procName'} - timestamp: ${app.timestamp || 'NO timestamp'}`);
                if (app.procName && (app.procName.toLowerCase().includes('telegram') || app.procName.toLowerCase().includes('viber'))) {
                  console.log(`  🔍 [${idx}] МЕССЕНДЖЕР ${app.procName}:`, {
                    procName: app.procName,
                    windowTitle: app.windowTitle,
                    hasWindowTitle: !!app.windowTitle,
                    windowTitleLength: app.windowTitle?.length || 0
                  });
                }
              });
            }
            return (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              position: 'relative',
              zIndex: 5,
            }}>
              {!filteredApplications || filteredApplications.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '15px',
                }}>
                  Нет данных об открытых приложениях
                </div>
              ) : (
                <div>
                  {/* Информация о количестве приложений */}
                  <div style={{
                    padding: '12px 16px',
                    background: 'var(--modal-local-pc-activity-details-app-card-background, rgba(36, 234, 102, 0.42))',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid var(--modal-local-pc-activity-details-app-card-border-color, black)',
                    textAlign: 'center',
                    color: headerColor,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}>
                    Всего приложений: {filteredApplications.length}
                    {filteredApplications.length > 3 && (
                      <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--modal-local-pc-activity-details-app-text-color, rgb(0, 0, 0))' }}>
                        Прокрутите вниз, чтобы увидеть все
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    maxHeight: 'calc(100vh - 350px)', 
                    overflowY: 'auto',
                    paddingRight: '4px',
                    WebkitOverflowScrolling: 'touch', // Плавная прокрутка на мобильных
                  }}>
                  {filteredApplications.map((app, idx) => {
                    // Уникальный ключ: используем комбинацию procName и индекса, чтобы избежать дубликатов
                    const uniqueKey = `app-${idx}-${app.procName || 'unknown'}-${app.timestamp || Date.now()}`;
                    return (
                    <div
                      key={uniqueKey}
                      style={{
                        background: 'var(--modal-local-pc-activity-details-app-card-background, rgba(225, 233, 225, 1))',
                        borderRadius: '12px',
                        padding: '16px',
                        border: '2px solid var(--modal-local-pc-activity-details-app-card-border-color, rgb(0, 0, 0))',
                        marginBottom: '12px',
                      }}
                    >
                      <div style={{
                        color: headerColor,
                        fontSize: '16px',
                        fontWeight: 700,
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <FiMonitor size={18} />
                        {app.procName || 'Unknown'}
                      </div>
                      {app.windowTitle && (() => {
                        // Для мессенджеров (Telegram, Viber) всегда показываем windowTitle,
                        // так как он содержит информацию о группе/чате
                        const isMessenger = app.procName && (
                          app.procName.toLowerCase().includes('telegram') ||
                          app.procName.toLowerCase().includes('viber') ||
                          app.procName.toLowerCase().includes('whatsapp') ||
                          app.procName.toLowerCase().includes('discord') ||
                          app.procName.toLowerCase().includes('skype')
                        );
                        // Показываем windowTitle если он отличается от procName ИЛИ это мессенджер
                        const shouldShow = isMessenger || app.windowTitle !== app.procName;
                        
                        // Очищаем windowTitle от невидимых символов (left-to-right mark, right-to-left mark и т.д.)
                        const cleanWindowTitle = app.windowTitle
                          .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '') // Удаляем направляющие символы
                          .trim();
                        
                        // Логирование для отладки мессенджеров
                        if (isMessenger) {
                          console.log(`🔍 [UserWorkTimeDetailsMobile] Рендер мессенджера ${app.procName}:`, {
                            procName: app.procName,
                            windowTitle: app.windowTitle,
                            cleanWindowTitle: cleanWindowTitle,
                            shouldShow,
                            isMessenger
                          });
                        }
                        
                        if (shouldShow && cleanWindowTitle) {
                          if (isMessenger) {
                            console.log(`✅ [UserWorkTimeDetailsMobile] РЕНДЕРИМ windowTitle для ${app.procName}:`, cleanWindowTitle);
                          }
                          return (
                            <div style={{
                              color: 'var(--modal-local-pc-activity-details-app-text-color, rgba(0, 0, 0, 0.7))',
                              fontSize: '14px',
                              marginBottom: '8px',
                              fontWeight: isMessenger ? 600 : 400,
                              display: 'block', // Явно указываем display
                              visibility: 'visible', // Явно указываем visibility
                              opacity: 1, // Явно указываем opacity
                            }}>
                              {cleanWindowTitle}
                            </div>
                          );
                        } else {
                          if (isMessenger) {
                            console.warn(`⚠️ [UserWorkTimeDetailsMobile] НЕ РЕНДЕРИМ windowTitle для ${app.procName}:`, {
                              shouldShow,
                              cleanWindowTitle,
                              hasCleanTitle: !!cleanWindowTitle
                            });
                          }
                          return null;
                        }
                      })()}
                      {app.timestamp && (
                        <div style={{
                          color: 'var(--modal-local-pc-activity-details-app-meta-text-color, rgb(7, 0, 0))',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                          <FiClock size={12} />
                          {formatTime(app.timestamp)}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Таб: Скриншоты */}
          {activeTab === 'screenshots' && (() => {
            console.log('📸 [UserWorkTimeDetailsMobile] Рендер вкладки Скриншоты');
            console.log('📸 [UserWorkTimeDetailsMobile] localScreenshots:', localScreenshots?.length || 0, localScreenshots);
            if (localScreenshots && localScreenshots.length > 0) {
              localScreenshots.forEach((shot, idx) => {
                console.log(`📸 [${idx}] Screenshot:`, {
                  id: shot.id,
                  url: shot.url,
                  filePath: shot.filePath,
                  timestamp: shot.timestamp,
                  fileSize: shot.fileSize
                });
              });
            }
            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Краткая статистика */}
              {activityStats && (
                <div style={{
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'var(--modal-local-pc-activity-details-period-block-background, rgba(0, 0, 0, 0.05))',
                  border: '1px solid var(--modal-local-pc-activity-details-period-block-border-color, rgb(0, 0, 0))',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    color: headerColor,
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}>
                    Активность за период
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--modal-local-pc-activity-details-period-text-color, rgba(134, 3, 3, 0.7))' }}>
                    {activityStats.totalActiveMinutes} мин активно / {activityStats.totalIdleMinutes} мин простоя
                  </div>
                </div>
              )}
              {!localScreenshots || localScreenshots.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgb(255, 255, 255)',
                  fontSize: '15px',
                }}>
                  Нет скриншотов
                </div>
              ) : (
                sortedScreenshots.map((shot, idx) => {
                  // Формируем правильный URL - убеждаемся, что он начинается с /
                  const screenshotUrl = shot.url && shot.url.startsWith('/') 
                    ? shot.url 
                    : shot.url && shot.url.startsWith('http')
                    ? shot.url
                    : `/uploads/screenshots/${shot.filePath ? shot.filePath.split(/[\/\\]/).pop() : shot.url || `screenshot_${idx}.jpg`}`;
                  
                  console.log(`📸 [UserWorkTimeDetailsMobile] Рендер скриншота ${idx}:`, {
                    originalUrl: shot.url,
                    filePath: shot.filePath,
                    finalUrl: screenshotUrl
                  });
                  
                  return (
                  <div
                    key={shot.id || `screenshot-${idx}`}
                    style={{
                      background: 'var(--modal-local-pc-activity-details-screenshot-card-background, rgba(255, 224, 130, 0.44))',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '2px solid var(--modal-local-pc-activity-details-screenshot-card-border-color, rgba(0, 0, 0, 0.3))',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                    }}>
                      <FiCamera size={20} color={headerColor} />
                      <div style={{
                        color: 'var(--modal-local-pc-activity-details-screenshot-text-color, rgba(0, 0, 0, 0.5))',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        <FiClock size={12} />
                        {formatTime(shot.timestamp)}
                      </div>
                      {shot.fileSize && (
                        <div style={{
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontSize: '11px',
                        }}>
                          {(shot.fileSize / 1024 / 1024).toFixed(2)} MB
                        </div>
                      )}
                      <div style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center',
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadScreenshot(screenshotUrl, shot.timestamp);
                          }}
                          style={{
                            background: 'var(--modal-local-pc-activity-details-screenshot-download-button-background, rgb(13, 232, 86))',
                            border: '1px solid rgb(0, 0, 0)',
                            color: 'var(--modal-local-pc-activity-details-screenshot-download-button-text-color, #ffffff)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'var(--modal-local-pc-activity-details-screenshot-download-button-background, rgba(67, 233, 123, 0.3))';
                            e.target.style.borderColor = '#43e97b';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'var(--modal-local-pc-activity-details-screenshot-download-button-background, rgba(67, 233, 123, 0.2))';
                            e.target.style.borderColor = 'rgba(67, 233, 123, 0.5)';
                          }}
                          title="Скачать скриншот"
                        >
                          <FiDownload size={14} />
                          Скачать
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintScreenshot(screenshotUrl, shot.timestamp);
                          }}
                          style={{
                            background: 'var(--modal-local-pc-activity-details-screenshot-print-button-background, rgb(0, 0, 0))',
                            border: '1px solid rgba(255, 224, 130, 0.5)',
                            color: 'var(--modal-local-pc-activity-details-screenshot-print-button-text-color, #ffe082)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: 600,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'var(--modal-local-pc-activity-details-screenshot-print-button-background, rgba(255, 224, 130, 0.3))';
                            e.target.style.borderColor = '#ffe082';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'var(--modal-local-pc-activity-details-screenshot-print-button-background, rgba(255, 224, 130, 0.2))';
                            e.target.style.borderColor = 'rgba(255, 224, 130, 0.5)';
                          }}
                          title="Печать / Сохранить в PDF"
                        >
                          <FiPrinter size={14} />
                          Печать
                        </button>
                      </div>
                    </div>
                    <div 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Формируем полный URL для скриншота, используя тот же метод, что и в handlePrintScreenshot
                        const fullScreenshotUrl = screenshotUrl.startsWith('http') 
                          ? screenshotUrl 
                          : `${window.location.origin}${screenshotUrl}`;
                        
                        console.log('📸 [UserWorkTimeDetailsMobile] Клик по скриншоту:', {
                          originalUrl: screenshotUrl,
                          fullUrl: fullScreenshotUrl,
                          windowOrigin: window.location.origin,
                          windowHref: window.location.href
                        });
                        
                        // Используем window.location.href вместо window.open, чтобы избежать перехвата маршрутизацией
                        // Или создаем временную ссылку и кликаем по ней
                        const link = document.createElement('a');
                        link.href = fullScreenshotUrl;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        background: 'rgba(0, 0, 0, 0.3)',
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={screenshotUrl}
                        alt={`Скриншот ${idx + 1}`}
                        onLoad={() => {
                          console.log(`✅ [UserWorkTimeDetailsMobile] Скриншот ${idx} загружен успешно:`, screenshotUrl);
                        }}
                        onError={(e) => {
                          console.error('❌ [UserWorkTimeDetailsMobile] Ошибка загрузки скриншота:', {
                            idx,
                            url: screenshotUrl,
                            originalUrl: shot.url,
                            filePath: shot.filePath,
                            shot
                          });
                          e.target.style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.style.cssText = 'padding: 40px 20px; text-align: center; color: rgba(255, 100, 100, 0.8);';
                          errorDiv.innerHTML = `
                            Не удалось загрузить скриншот<br/>
                            <small style="font-size: 11px; color: rgba(255, 255, 255, 0.4);">URL: ${screenshotUrl}</small><br/>
                            <small style="font-size: 10px; color: rgba(255, 255, 255, 0.3);">Path: ${shot.filePath || 'N/A'}</small>
                          `;
                          e.target.parentElement.appendChild(errorDiv);
                        }}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          cursor: 'pointer',
                          pointerEvents: 'none', // Предотвращаем клики по самому изображению
                        }}
                      />
                    </div>
                  </div>
                  );
                })
              )}
            </div>
            );
          })()}
        </div>

        {ENABLE_MODAL_SWIPE_BACK && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 20002,
            }}
          >
            ← Свайпните влево для возврата
          </div>
        )}
      </div>

      {/* Диалог выбора элементов для печати */}
      {showPrintDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          zIndex: 100002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }} onClick={() => setShowPrintDialog(false)}>
          <div style={{
            background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            border: '2px solid rgba(255, 224, 130, 0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              color: '#ffe082',
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <FiPrinter size={20} />
              Печать отчета
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', marginBottom: '15px' }}>
                Выберите элементы для печати:
              </p>
              
              {[
                { key: 'events', label: 'События (входы/выходы)', count: sortedLogs?.length || 0 },
                { key: 'applications', label: 'Приложения', count: filteredApplications?.length || 0 },
                { key: 'urls', label: 'Сайты', count: filteredUrls?.length || 0 },
                { key: 'screenshots', label: 'Скриншоты', count: sortedScreenshots?.length || 0 },
              ].map(option => (
                <label key={option.key} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  cursor: 'pointer',
                  border: printOptions[option.key] ? '2px solid rgba(255, 224, 130, 0.5)' : '2px solid transparent',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="checkbox"
                    checked={printOptions[option.key]}
                    onChange={(e) => setPrintOptions(prev => ({
                      ...prev,
                      [option.key]: e.target.checked
                    }))}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>
                      {option.label}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                      {option.count} элементов
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPrintDialog(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  if (!printOptions.events && !printOptions.applications && !printOptions.urls && !printOptions.screenshots) {
                    alert('Выберите хотя бы один элемент для печати');
                    return;
                  }
                  handlePrintReport();
                  setShowPrintDialog(false);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #2193b0 0%, #43e97b 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FiPrinter size={16} />
                Печать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

